import { FC, useState, useEffect } from 'react'

import styled from 'styled-components'
import { useDisclosure } from '@mantine/hooks'
import { useDebounce } from 'use-debounce'
import { useForm } from 'react-hook-form'
import { notifications } from '@mantine/notifications'

import {
  CreateUserBody,
  ManagedUser,
  UpdateUserBody,
  UserRole,
  UserStatus,
  useArchiveUserMutation,
  useChangeUserRoleMutation,
  useCreateUserMutation,
  useGetUsersForManagementQuery,
  useResetUserPasswordMutation,
  useRestoreUserMutation,
  useUpdateUserMutation,
} from 'src/Redux/api/usersApi.ts'
import { Role, useGetProfileQuery } from 'src/Redux/api/authApi.ts'
import { PageTitle } from 'src/Components/PageTitle/index.tsx'
import { colors, device } from 'src/Helpers/constants.ts'

import { SearchBlock } from './SearchBlock.tsx'
import { TableHeader } from './TableHeader.tsx'
import { UsersTable } from './UsersTable.tsx'
import { AddUserModal } from './AddUserModal.tsx'
import { EditUserModal } from './EditUserModal.tsx'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 36px;
  position: relative;
  height: 100%;
  overflow: hidden;
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 50px;
  position: static;

  @media ${device.tabletMax} {
    gap: 20px;
    width: 100%;

    div {
      width: 100%;
    }
  }
`

export interface SearchUserFormData {
  searchString: string
}

const AccessDenied = styled.span`
  font-size: 18px;
  color: ${colors.thirdGray};
  font-weight: 600;
`

const showError = (title: string, error: unknown): void => {
  const err = error as { data?: { error?: string } | string; message?: string }
  notifications.show({
    title,
    message:
      (typeof err?.data === 'object' ? err?.data?.error : err?.data) ||
      err?.message ||
      'Произошла неизвестная ошибка',
    color: 'red',
    position: 'top-right',
  })
}

export const UsersPage: FC = () => {
  const [isAddModalOpened, { open: openAddModal, close: closeAddModal }] =
    useDisclosure(false)
  const [isEditModalOpened, { open: openEditModal, close: closeEditModal }] =
    useDisclosure(false)

  const [pageNumber, setPageNumber] = useState(1)
  const [userToEdit, setUserToEdit] = useState<ManagedUser | null>(null)
  const [roleFilter, setRoleFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<UserStatus>(
    UserStatus.ACTIVE,
  )

  const { control, watch } = useForm<SearchUserFormData>()
  const { searchString } = watch()
  const [debouncedSearchValue] = useDebounce(searchString, 1000)

  useEffect(() => {
    setPageNumber(1)
  }, [debouncedSearchValue, roleFilter, statusFilter])

  const { data: profile } = useGetProfileQuery()

  const { data: usersResponse, isLoading } = useGetUsersForManagementQuery(
    {
      search: debouncedSearchValue,
      role: roleFilter ? (roleFilter as UserRole) : undefined,
      status: statusFilter,
      pageNumber,
      limit: 20,
    },
    { skip: profile?.role !== Role.ADMIN },
  )

  const [createUser] = useCreateUserMutation()
  const [updateUser] = useUpdateUserMutation()
  const [changeUserRole] = useChangeUserRoleMutation()
  const [resetUserPassword] = useResetUserPasswordMutation()
  const [archiveUser] = useArchiveUserMutation()
  const [restoreUser] = useRestoreUserMutation()

  if (profile && profile.role !== Role.ADMIN) {
    return <AccessDenied>Недостаточно прав для просмотра страницы</AccessDenied>
  }

  const onAddUser = (user: CreateUserBody): void => {
    createUser(user)
      .unwrap()
      .then(() => {
        closeAddModal()
        notifications.show({
          title: 'Пользователь успешно создан',
          message: '',
          position: 'top-right',
        })
      })
      .catch((error) => showError('Ошибка при создании пользователя', error))
  }

  const onUpdateUser = (id: number, data: UpdateUserBody): void => {
    updateUser({ id, data })
      .unwrap()
      .then(() => {
        closeEditModal()
        notifications.show({
          title: 'Данные пользователя обновлены',
          message: '',
          position: 'top-right',
        })
      })
      .catch((error) => showError('Ошибка при обновлении данных', error))
  }

  const onChangeRole = (id: number, role: UserRole): void => {
    changeUserRole({ id, role })
      .unwrap()
      .then(() => {
        closeEditModal()
        notifications.show({
          title: 'Роль пользователя изменена',
          message: '',
          position: 'top-right',
        })
      })
      .catch((error) => showError('Ошибка при изменении роли', error))
  }

  const onResetPassword = (id: number, password: string): void => {
    resetUserPassword({ id, password })
      .unwrap()
      .then(() => {
        closeEditModal()
        notifications.show({
          title: 'Пароль успешно сброшен',
          message: '',
          position: 'top-right',
        })
      })
      .catch((error) => showError('Ошибка при сбросе пароля', error))
  }

  const onArchiveUser = (id: number): void => {
    archiveUser(id)
      .unwrap()
      .then(() => {
        notifications.show({
          title: 'Пользователь архивирован',
          message: '',
          position: 'top-right',
        })
      })
      .catch((error) => showError('Ошибка при архивировании', error))
  }

  const onRestoreUser = (id: number): void => {
    restoreUser(id)
      .unwrap()
      .then(() => {
        notifications.show({
          title: 'Пользователь восстановлен',
          message: '',
          position: 'top-right',
        })
      })
      .catch((error) => showError('Ошибка при восстановлении', error))
  }

  const onLoadMore = (): void => {
    setPageNumber(pageNumber + 1)
  }

  const onClickOpenEditModal = (user: ManagedUser): void => {
    setUserToEdit(user)
    openEditModal()
  }

  return (
    <>
      <AddUserModal
        onClose={closeAddModal}
        onAddUser={onAddUser}
        isOpened={isAddModalOpened}
      />

      <EditUserModal
        onClose={closeEditModal}
        isOpened={isEditModalOpened}
        userToEdit={userToEdit}
        onUpdateUser={onUpdateUser}
        onChangeRole={onChangeRole}
        onResetPassword={onResetPassword}
      />

      <Container>
        <TitleWrapper>
          <PageTitle>пользователи</PageTitle>

          <SearchBlock
            onOpenAddModal={openAddModal}
            control={control}
            roleFilter={roleFilter}
            onRoleFilterChange={setRoleFilter}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
          />
        </TitleWrapper>
        <TableHeader />
        <UsersTable
          users={usersResponse?.data}
          isLoading={isLoading}
          onEditUser={onClickOpenEditModal}
          onArchiveUser={onArchiveUser}
          onRestoreUser={onRestoreUser}
          onLoadMore={onLoadMore}
          hasNextPage={usersResponse?.meta.hasNextPage}
          currentUserId={profile?.id}
        />
      </Container>
    </>
  )
}
